import {
  Fragment,
  useEffect,
  useState,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
} from 'react';

/**
 * The console's one table (P11.7).
 *
 * Every list answers three questions in its row — what is it, is it a problem,
 * what can I do — so the columns are chosen per page, and the table does the
 * rest the same way everywhere: headers that sort, numbers aligned right and in
 * tabular figures, a row that opens its detail (`onSelect`) by click or Enter,
 * and a message instead of an empty grid.
 */

export interface Column<T> {
  key: string;
  header: string;
  cell: (row: T) => ReactNode;
  /** What the column sorts by; absent, it does not sort. */
  sortValue?: (row: T) => number | string;
  /** `end` for numbers. */
  align?: 'start' | 'end';
  /** Hidden below 48rem, for detail the side panel shows anyway. */
  wideOnly?: boolean;
  /** The row's name: rendered as its header cell, which a screen reader announces. */
  rowHeader?: boolean;
}

export interface DataTableProps<T> {
  caption: string;
  columns: readonly Column<T>[];
  rows: readonly T[];
  rowKey: (row: T) => string;
  onSelect?: ((row: T) => void) | undefined;
  /** Which row is open, so it stays marked while its panel is. */
  selectedKey?: string | undefined;
  initialSort?: { key: string; direction: 'asc' | 'desc' } | undefined;
  empty?: ReactNode;
  /**
   * Related information for a row, shown under it when a person opens it with the
   * chevron. Keeps the row to what it is and whether it is a problem, and the
   * rest one click away without leaving the list.
   */
  renderDetail?: ((row: T) => ReactNode) | undefined;
  /**
   * Pages of rows with numbered links and a choice of how many to show. A list
   * the server hands out in parts passes `more`: the table asks for the next part
   * on its own as a person nears the last page it has.
   */
  pagination?: Pagination | undefined;
}

export interface Pagination {
  /** Rows per page to offer; the first is the start. */
  sizes?: readonly number[];
  /** The server holds more rows than `rows` does. */
  hasMore?: boolean;
  loadingMore?: boolean;
  onLoadMore?: () => void;
}

const DEFAULT_SIZES = [25, 50, 100] as const;

/** Page numbers to show: the ends, and the neighbours of the current one. */
function pageLinks(page: number, pages: number): (number | 'gap')[] {
  const wanted = new Set([0, pages - 1, page - 1, page, page + 1]);
  const shown = [...wanted].filter((n) => n >= 0 && n < pages).sort((a, b) => a - b);
  const out: (number | 'gap')[] = [];
  shown.forEach((n, index) => {
    const before = shown[index - 1];
    if (before !== undefined && n - before > 1) out.push('gap');
    out.push(n);
  });
  return out;
}

export function DataTable<T>({
  caption,
  columns,
  rows,
  rowKey,
  onSelect,
  selectedKey,
  initialSort,
  empty,
  renderDetail,
  pagination,
}: DataTableProps<T>): ReactNode {
  const sizes = pagination?.sizes ?? DEFAULT_SIZES;
  const [pageSize, setPageSize] = useState<number>(sizes[0] ?? DEFAULT_SIZES[0]);
  const [page, setPage] = useState(0);
  const [sort, setSort] = useState(initialSort);
  const [openKeys, setOpenKeys] = useState<ReadonlySet<string>>(new Set());
  const column = columns.find((each) => each.key === sort?.key);
  const sorted =
    column?.sortValue === undefined || sort === undefined
      ? rows
      : [...rows].sort((a, b) => {
          const left = column.sortValue?.(a) ?? '';
          const right = column.sortValue?.(b) ?? '';
          const order =
            typeof left === 'number' && typeof right === 'number'
              ? left - right
              : String(left).localeCompare(String(right));
          return sort.direction === 'asc' ? order : -order;
        });

  const paged = pagination !== undefined;
  const pages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const current = Math.min(page, pages - 1);
  const shownRows = paged ? sorted.slice(current * pageSize, (current + 1) * pageSize) : sorted;

  // Near the last page the table holds, fetch the next part so there is one to turn to.
  const nearEnd = paged && current >= pages - 1;
  const { hasMore, loadingMore, onLoadMore } = pagination ?? {};
  useEffect(() => {
    if (nearEnd && hasMore === true && loadingMore !== true) onLoadMore?.();
  }, [nearEnd, hasMore, loadingMore, onLoadMore, sorted.length]);

  if (rows.length === 0 && empty !== undefined) return <>{empty}</>;

  const toggle = (key: string): void => {
    setPage(0);
    setSort((current) =>
      current?.key === key
        ? { key, direction: current.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: 'desc' },
    );
  };

  const toggleOpen = (key: string): void => {
    setOpenKeys((current) => {
      const next = new Set(current);
      if (!next.delete(key)) next.add(key);
      return next;
    });
  };

  const open = (row: T) => (event: KeyboardEvent<HTMLTableRowElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onSelect?.(row);
    }
  };

  return (
    <div className="table-wrap">
      <table
        className={`table data-table${onSelect === undefined ? '' : ' data-table-selectable'}`}
      >
        <caption className="visually-hidden">{caption}</caption>
        <thead>
          <tr>
            {renderDetail !== undefined && (
              <th scope="col" className="expand-cell">
                <span className="visually-hidden">Details</span>
              </th>
            )}
            {columns.map((each) => (
              <th
                key={each.key}
                scope="col"
                className={[
                  each.align === 'end' ? 'num' : '',
                  each.wideOnly === true ? 'wide-only' : '',
                ].join(' ')}
                aria-sort={
                  sort?.key === each.key
                    ? sort.direction === 'asc'
                      ? 'ascending'
                      : 'descending'
                    : undefined
                }
              >
                {each.sortValue === undefined ? (
                  each.header
                ) : (
                  <button
                    type="button"
                    className="sort-button"
                    onClick={() => {
                      toggle(each.key);
                    }}
                  >
                    {each.header}
                    <span aria-hidden="true" className="sort-mark">
                      {sort?.key === each.key ? (sort.direction === 'asc' ? '↑' : '↓') : '↕'}
                    </span>
                  </button>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {shownRows.map((row) => {
            const key = rowKey(row);
            const isOpen = renderDetail !== undefined && openKeys.has(key);
            return (
              <Fragment key={key}>
                <tr
                  className={
                    [selectedKey === key ? 'is-selected' : '', isOpen ? 'is-expanded' : '']
                      .join(' ')
                      .trim() || undefined
                  }
                  {...(onSelect === undefined
                    ? {}
                    : {
                        tabIndex: 0,
                        onClick: (event: MouseEvent<HTMLTableRowElement>) => {
                          // A link or button in the row does its own thing.
                          if ((event.target as HTMLElement).closest('a, button') !== null) return;
                          onSelect(row);
                        },
                        onKeyDown: open(row),
                      })}
                >
                  {renderDetail !== undefined && (
                    <td className="expand-cell">
                      <button
                        type="button"
                        className="expand-button"
                        aria-expanded={isOpen}
                        aria-label={isOpen ? 'Hide details' : 'Show details'}
                        onClick={() => {
                          toggleOpen(key);
                        }}
                      >
                        <span aria-hidden="true">{isOpen ? '▾' : '▸'}</span>
                      </button>
                    </td>
                  )}
                  {columns.map((each) => {
                    const className = [
                      each.align === 'end' ? 'num' : '',
                      each.wideOnly === true ? 'wide-only' : '',
                    ].join(' ');
                    return each.rowHeader === true ? (
                      <th key={each.key} scope="row" className={className}>
                        {each.cell(row)}
                      </th>
                    ) : (
                      <td key={each.key} className={className}>
                        {each.cell(row)}
                      </td>
                    );
                  })}
                </tr>
                {isOpen && (
                  <tr className="detail-row">
                    <td colSpan={columns.length + 1}>{renderDetail(row)}</td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
      {paged && (
        <nav className="pager" aria-label={`${caption}: pages`}>
          <span className="pager-count muted text-sm" aria-live="polite">
            {sorted.length === 0
              ? 'None'
              : `${String(current * pageSize + 1)}–${String(Math.min(sorted.length, (current + 1) * pageSize))} of ${sorted.length.toLocaleString()}${hasMore === true ? '+' : ''}`}
            {loadingMore === true ? ' · loading…' : ''}
          </span>
          <span className="pager-controls">
            <label className="pager-size text-sm muted">
              Rows per page
              <select
                value={pageSize}
                onChange={(event) => {
                  setPageSize(Number(event.target.value));
                  setPage(0);
                }}
              >
                {sizes.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </label>
            <span className="pager-pages">
              <button
                type="button"
                className="pager-button"
                disabled={current === 0}
                onClick={() => {
                  setPage(current - 1);
                }}
              >
                Previous
              </button>
              {pageLinks(current, pages).map((entry, index) =>
                entry === 'gap' ? (
                  <span key={`gap-${String(index)}`} className="pager-gap" aria-hidden="true">
                    …
                  </span>
                ) : (
                  <button
                    key={entry}
                    type="button"
                    className="pager-button"
                    aria-current={entry === current ? 'page' : undefined}
                    aria-label={`Page ${String(entry + 1)}`}
                    onClick={() => {
                      setPage(entry);
                    }}
                  >
                    {entry + 1}
                  </button>
                ),
              )}
              <button
                type="button"
                className="pager-button"
                disabled={current >= pages - 1}
                onClick={() => {
                  setPage(current + 1);
                }}
              >
                Next
              </button>
            </span>
          </span>
        </nav>
      )}
    </div>
  );
}

/** A number and its share of a whole, as a bar beside it. */
export function ShareBar({ value, total }: { value: number; total: number }): ReactNode {
  const share = total <= 0 ? 0 : Math.min(1, value / total);
  return (
    <span className="share">
      <span className="share-value">{value.toLocaleString()}</span>
      <span className="share-bar" aria-hidden="true">
        <span style={{ width: `${String(Math.round(share * 100))}%` }} />
      </span>
      <span className="share-pct">{Math.round(share * 100)}%</span>
    </span>
  );
}

/** Label and value pairs, for a panel's facts. */
export function Facts({ items }: { items: readonly [string, ReactNode][] }): ReactNode {
  return (
    <dl className="facts">
      {items.map(([label, value]) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}
